import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { settingsAPI, accountAPI } from "../lib/api";
import { useAuthStore } from "../lib/store";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Settings,
  Loader2,
  Plus,
  X,
  Palette,
  Target,
  Hash,
  ShieldAlert,
  Save,
  FileText,
  ArrowRight,
  Captions,
  Trash2,
  AlertTriangle,
  Eye,
} from "lucide-react";
import { CaptionStyleSelector } from "../components/CaptionStyleSelector";
import { VoiceCloneManager } from "../components/VoiceCloneManager";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function SettingsPage() {
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.logout);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [hashtagInput, setHashtagInput] = useState("");
  const [prohibitedInput, setProhibitedInput] = useState("");
  const [personaInput, setPersonaInput] = useState("");

  const [guidelines, setGuidelines] = useState({
    brand_voice: "casual",
    tone: "friendly",
    target_audience: "",
    preferred_hashtags: [],
    prohibited_words: [],
    engagement_personas: [],
    // Caption settings
    default_caption_style: "classic",
    caption_font_size: "medium",
    caption_position: "bottom",
    auto_captions_enabled: true,
    // Privacy & Sharing
    consent_to_showcase: false,
  });

  useEffect(() => {
    const fetchGuidelines = async () => {
      try {
        const response = await settingsAPI.getBrandGuidelines();
        setGuidelines(response.data);
      } catch (error) {
        console.error("Failed to fetch guidelines:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchGuidelines();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.updateBrandGuidelines(guidelines);
      toast.success("Brand guidelines saved!");
    } catch (error) {
      toast.error("Failed to save guidelines");
    } finally {
      setSaving(false);
    }
  };

  const addItem = (field, input, setInput) => {
    if (input && !guidelines[field].includes(input)) {
      setGuidelines((prev) => ({
        ...prev,
        [field]: [...prev[field], input],
      }));
      setInput("");
    }
  };

  const removeItem = (field, item) => {
    setGuidelines((prev) => ({
      ...prev,
      [field]: prev[field].filter((i) => i !== item),
    }));
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE") {
      toast.error("Please type DELETE to confirm");
      return;
    }
    
    setDeleting(true);
    try {
      await accountAPI.deleteAccount();
      toast.success("Your account has been permanently deleted");
      // Log out and redirect to landing page
      logout();
      navigate("/");
    } catch (error) {
      console.error("Failed to delete account:", error);
      const message = error.response?.data?.detail || "Failed to delete account. Please try again.";
      toast.error(message);
    } finally {
      setDeleting(false);
      setDeleteConfirmText("");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="max-w-4xl mx-auto space-y-8"
      data-testid="settings-page"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <Settings className="w-8 h-8 text-[#00F0FF]" />
            Brand Settings
          </h1>
          <p className="text-muted-foreground mt-2">
            Configure your brand voice, tone, and content guidelines.
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B] font-bold rounded-xl gap-2 neon-glow"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save Changes
        </Button>
      </div>

      {/* Brand Voice & Tone */}
      <motion.div variants={itemVariants}>
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Palette className="w-5 h-5 text-[#00F0FF]" />
              Brand Voice & Tone
            </CardTitle>
            <CardDescription>
              Define how your content should sound and feel.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-white">Brand Voice</Label>
                <Select
                  value={guidelines.brand_voice}
                  onValueChange={(value) =>
                    setGuidelines({ ...guidelines, brand_voice: value })
                  }
                >
                  <SelectTrigger className="h-12 bg-secondary/50 border-border/50" data-testid="brand-voice-setting">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="casual">Casual & Relaxed</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="humorous">Humorous & Fun</SelectItem>
                    <SelectItem value="inspirational">Inspirational</SelectItem>
                    <SelectItem value="educational">Educational</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-white">Tone</Label>
                <Select
                  value={guidelines.tone}
                  onValueChange={(value) =>
                    setGuidelines({ ...guidelines, tone: value })
                  }
                >
                  <SelectTrigger className="h-12 bg-secondary/50 border-border/50" data-testid="tone-setting">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="friendly">Friendly</SelectItem>
                    <SelectItem value="energetic">Energetic</SelectItem>
                    <SelectItem value="calm">Calm</SelectItem>
                    <SelectItem value="authoritative">Authoritative</SelectItem>
                    <SelectItem value="playful">Playful</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Caption Settings */}
      <motion.div variants={itemVariants}>
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Captions className="w-5 h-5 text-[#A855F7]" />
              Caption Settings
            </CardTitle>
            <CardDescription>
              Set your default caption style for all videos. You can also change this per video.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Auto Captions Toggle */}
            <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
              <div>
                <Label className="text-white font-medium">Auto-generate Captions</Label>
                <p className="text-muted-foreground text-sm mt-1">
                  Automatically add captions to all new videos
                </p>
              </div>
              <Switch
                checked={guidelines.auto_captions_enabled}
                onCheckedChange={(checked) =>
                  setGuidelines({ ...guidelines, auto_captions_enabled: checked })
                }
                data-testid="auto-captions-toggle"
              />
            </div>

            {/* Caption Style Selection */}
            <div className="space-y-3">
              <Label className="text-white">Default Caption Style</Label>
              <CaptionStyleSelector
                value={guidelines.default_caption_style}
                onChange={(style) =>
                  setGuidelines({ ...guidelines, default_caption_style: style })
                }
                showPreview={true}
              />
            </div>

            {/* Font Size & Position */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-white">Font Size</Label>
                <Select
                  value={guidelines.caption_font_size}
                  onValueChange={(value) =>
                    setGuidelines({ ...guidelines, caption_font_size: value })
                  }
                >
                  <SelectTrigger className="h-12 bg-secondary/50 border-border/50" data-testid="caption-font-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Small</SelectItem>
                    <SelectItem value="medium">Medium (Recommended)</SelectItem>
                    <SelectItem value="large">Large</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-white">Position</Label>
                <Select
                  value={guidelines.caption_position}
                  onValueChange={(value) =>
                    setGuidelines({ ...guidelines, caption_position: value })
                  }
                >
                  <SelectTrigger className="h-12 bg-secondary/50 border-border/50" data-testid="caption-position">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="top">Top</SelectItem>
                    <SelectItem value="center">Center</SelectItem>
                    <SelectItem value="bottom">Bottom (Recommended)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Voice Cloning */}
      <motion.div variants={itemVariants}>
        <VoiceCloneManager />
      </motion.div>

      {/* Target Audience */}
      <motion.div variants={itemVariants}>
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-[#FF0055]" />
              Target Audience
            </CardTitle>
            <CardDescription>
              Describe your ideal viewer to help AI generate relevant content.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="e.g., Gen Z creators interested in lifestyle content, tech enthusiasts aged 18-35..."
              value={guidelines.target_audience}
              onChange={(e) =>
                setGuidelines({ ...guidelines, target_audience: e.target.value })
              }
              className="min-h-[100px] bg-secondary/50 border-border/50"
              data-testid="target-audience-textarea"
            />
          </CardContent>
        </Card>
      </motion.div>

      {/* Preferred Hashtags */}
      <motion.div variants={itemVariants}>
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Hash className="w-5 h-5 text-[#00FF94]" />
              Preferred Hashtags
            </CardTitle>
            <CardDescription>
              Hashtags that will be automatically suggested for your videos.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Add hashtag..."
                value={hashtagInput}
                onChange={(e) => setHashtagInput(e.target.value)}
                onKeyPress={(e) =>
                  e.key === "Enter" &&
                  addItem("preferred_hashtags", hashtagInput, setHashtagInput)
                }
                className="h-10 bg-secondary/50 border-border/50"
                data-testid="hashtag-setting-input"
              />
              <Button
                onClick={() =>
                  addItem("preferred_hashtags", hashtagInput, setHashtagInput)
                }
                variant="outline"
                size="icon"
                className="h-10 w-10 border-border/50"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {guidelines.preferred_hashtags.map((tag) => (
                <Badge
                  key={tag}
                  variant="outline"
                  className="badge-success gap-1 cursor-pointer hover:bg-destructive/20"
                  onClick={() => removeItem("preferred_hashtags", tag)}
                >
                  #{tag.replace("#", "")}
                  <X className="w-3 h-3 ml-1" />
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Prohibited Words */}
      <motion.div variants={itemVariants}>
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-[#FF0055]" />
              Prohibited Words
            </CardTitle>
            <CardDescription>
              Words that should never appear in your generated content.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Add prohibited word..."
                value={prohibitedInput}
                onChange={(e) => setProhibitedInput(e.target.value)}
                onKeyPress={(e) =>
                  e.key === "Enter" &&
                  addItem("prohibited_words", prohibitedInput, setProhibitedInput)
                }
                className="h-10 bg-secondary/50 border-border/50"
                data-testid="prohibited-word-input"
              />
              <Button
                onClick={() =>
                  addItem("prohibited_words", prohibitedInput, setProhibitedInput)
                }
                variant="outline"
                size="icon"
                className="h-10 w-10 border-border/50"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {guidelines.prohibited_words.map((word) => (
                <Badge
                  key={word}
                  variant="outline"
                  className="badge-viral gap-1 cursor-pointer hover:bg-destructive/20"
                  onClick={() => removeItem("prohibited_words", word)}
                >
                  {word}
                  <X className="w-3 h-3 ml-1" />
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Engagement Personas */}
      <motion.div variants={itemVariants}>
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-[#A855F7]" />
              Engagement Personas
            </CardTitle>
            <CardDescription>
              Define different personas for automated engagement responses.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="e.g., Friendly Creator, Expert Helper..."
                value={personaInput}
                onChange={(e) => setPersonaInput(e.target.value)}
                onKeyPress={(e) =>
                  e.key === "Enter" &&
                  addItem("engagement_personas", personaInput, setPersonaInput)
                }
                className="h-10 bg-secondary/50 border-border/50"
                data-testid="persona-input"
              />
              <Button
                onClick={() =>
                  addItem("engagement_personas", personaInput, setPersonaInput)
                }
                variant="outline"
                size="icon"
                className="h-10 w-10 border-border/50"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {guidelines.engagement_personas.map((persona) => (
                <Badge
                  key={persona}
                  variant="outline"
                  className="bg-[#A855F7]/20 text-[#A855F7] border-[#A855F7]/50 gap-1 cursor-pointer hover:bg-destructive/20"
                  onClick={() => removeItem("engagement_personas", persona)}
                >
                  {persona}
                  <X className="w-3 h-3 ml-1" />
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Privacy & Sharing */}
      <motion.div variants={itemVariants}>
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#00F0FF]" />
              Privacy & Sharing
            </CardTitle>
            <CardDescription>
              Control how your content can be used and displayed.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Video Showcase Consent */}
            <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
              <div className="pr-4">
                <Label className="text-white font-medium">Allow Video Showcase</Label>
                <p className="text-muted-foreground text-sm mt-1">
                  Opt-in to let your best videos be featured in our public demo gallery. 
                  Only admin-selected videos from consenting users will be displayed.
                </p>
              </div>
              <Switch
                checked={guidelines.consent_to_showcase}
                onCheckedChange={(checked) =>
                  setGuidelines({ ...guidelines, consent_to_showcase: checked })
                }
                data-testid="consent-to-showcase-toggle"
              />
            </div>
            {guidelines.consent_to_showcase && (
              <div className="p-3 bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-lg">
                <p className="text-sm text-[#00F0FF]">
                  🎉 Thank you for allowing us to showcase your work! Our admins may feature your 
                  high-quality videos on the landing page to inspire other creators.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Legal Section */}
      <motion.div variants={itemVariants}>
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-muted-foreground" />
              Legal
            </CardTitle>
            <CardDescription>
              Review our terms and policies.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <a 
              href="/terms" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-between p-3 rounded-lg bg-secondary/20 hover:bg-secondary/40 transition-colors group"
            >
              <span className="text-sm text-white">Terms of Service</span>
              <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-[#00F0FF] transition-colors" />
            </a>
            <a 
              href="/privacy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-between p-3 rounded-lg bg-secondary/20 hover:bg-secondary/40 transition-colors group"
            >
              <span className="text-sm text-white">Privacy Policy</span>
              <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-[#00F0FF] transition-colors" />
            </a>
          </CardContent>
        </Card>
      </motion.div>

      {/* Danger Zone - Account Deletion */}
      <motion.div variants={itemVariants}>
        <Card className="bg-card border-red-500/30">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Danger Zone
            </CardTitle>
            <CardDescription>
              Irreversible actions that affect your account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h4 className="text-white font-medium">Delete Account</h4>
                  <p className="text-muted-foreground text-sm mt-1">
                    Permanently delete your account and all associated data including videos, voice clones, and settings.
                  </p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="destructive" 
                      className="gap-2 shrink-0"
                      data-testid="delete-account-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete Account
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-card border-border">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-white flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                        Delete Your Account?
                      </AlertDialogTitle>
                      <AlertDialogDescription className="text-muted-foreground space-y-3">
                        <p>
                          This action <strong className="text-red-400">cannot be undone</strong>. This will permanently delete:
                        </p>
                        <ul className="list-disc list-inside space-y-1 text-sm">
                          <li>Your account and profile information</li>
                          <li>All your generated videos</li>
                          <li>Voice clones and audio files</li>
                          <li>TikTok and Instagram connections</li>
                          <li>Brand settings and preferences</li>
                          <li>Scheduled posts and calendar items</li>
                        </ul>
                        <div className="pt-2">
                          <Label htmlFor="delete-confirm" className="text-white text-sm">
                            Type <strong className="text-red-400">DELETE</strong> to confirm:
                          </Label>
                          <Input
                            id="delete-confirm"
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            placeholder="DELETE"
                            className="mt-2 bg-secondary/50 border-red-500/30 focus:border-red-500"
                            data-testid="delete-confirm-input"
                          />
                        </div>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel 
                        className="bg-secondary hover:bg-secondary/80"
                        onClick={() => setDeleteConfirmText("")}
                      >
                        Cancel
                      </AlertDialogCancel>
                      <Button
                        variant="destructive"
                        onClick={handleDeleteAccount}
                        disabled={deleteConfirmText !== "DELETE" || deleting}
                        data-testid="confirm-delete-btn"
                      >
                        {deleting ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Deleting...
                          </>
                        ) : (
                          "Delete My Account"
                        )}
                      </Button>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
}
